import { useCallback, useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { useStickyAutoScroll } from '@hooks/use-sticky-auto-scroll';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { IMAGE_EXTENSIONS } from '@utils/assets';
import { pxToRem } from '@utils/converters';
import { uuid } from '@utils/uuid';

import {
  useAcceptPlan,
  useAnswerQuestions,
  useDeleteAIBuilderHistory,
  useInterruptAIBuilder,
  useRejectPlan,
  useRunAIBuilder,
  useUpdateSessionMode,
} from 'api/ai-builder/hooks';
import { AIBuilderMode, QuestionAnswer } from 'api/ai-builder/requests';

import { ChatHeader, InputBox, MessagesContainer } from './components';
import {
  useAIBuilderChat,
  useAIBuilderWebSocket,
  useAutoResizeTextarea,
  useFileUpload,
  useLoadingState,
  type ChatMessage,
} from './hooks';

import { useNodes } from '../hooks/use-nodes';

const DEFAULT_INPUT_ROW_HEIGHT = 72;

export const AiBuilderChat = ({
  journeyShortId,
  onClose,
}: {
  journeyShortId: string;
  onClose?: () => void;
}) => {
  const {
    chatId,
    messages: hydrationMessages,
    mode,
    clearChat,
    isGenerating,
    reduceByWsEvent,
    startRequest,
    endRequest,
  } = useAIBuilderChat({ journeyShortId });
  const { t } = useScopedTranslate(locale);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const messagesInnerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const inputRowRef = useRef<HTMLDivElement | null>(null);
  const buttonsRowRef = useRef<HTMLDivElement | null>(null);

  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputRowHeight, setInputRowHeight] = useState(DEFAULT_INPUT_ROW_HEIGHT);
  const {
    loadingLabel,
    isBuilding,
    setThinking,
    handleLoadingStateEvent,
    clearLoadingLabel,
    setBuilding,
    resetToIdle,
  } = useLoadingState();
  const {
    uploadedFiles,
    fileError,
    handleFileSelected,
    handleRemoveFile,
    clearFiles: clearUploadedFiles,
    clearError: clearFileError,
    setError: setFileError,
  } = useFileUpload({
    allowedExtensions: IMAGE_EXTENSIONS,
    getErrorMessage: (extension) => t('onlyImagesSupported', { extension }),
  });

  const {
    onScroll: handleScroll,
    resetSticky,
    pauseStickyForFrame,
    scrollToBottom,
  } = useStickyAutoScroll({
    containerRef: messagesRef,
    contentRef: messagesInnerRef,
    resetKey: chatId,
  });

  const runPrompt = useRunAIBuilder();
  const interruptPrompt = useInterruptAIBuilder();
  const answerQuestionsMutation = useAnswerQuestions();
  const deleteHistoryMutation = useDeleteAIBuilderHistory(journeyShortId, chatId!);
  const updateModeMutation = useUpdateSessionMode();
  const acceptPlanMutation = useAcceptPlan();
  const rejectPlanMutation = useRejectPlan();
  const { selectedNode } = useNodes();

  const handleModeChange = useCallback(
    (newMode: AIBuilderMode) => {
      updateModeMutation.mutate({
        journeyShortId,
        chatId: chatId!,
        mode: newMode,
      });
    },
    [updateModeMutation, journeyShortId, chatId]
  );

  const resetToIdleState = useCallback(() => {
    resetToIdle();
    endRequest();
  }, [endRequest, resetToIdle]);

  const { clearActiveToolCalls } = useAIBuilderWebSocket({
    journeyShortId,
    reduceByWsEvent,
    onMessagesUpdate: setMessages,
    onLoadingStateEvent: handleLoadingStateEvent,
    onClearLoadingLabel: clearLoadingLabel,
    onToolStart: setBuilding,
    onRequestEnd: resetToIdleState,
  });

  const handleInterruptRequest = useCallback(() => {
    if (!chatId) return;
    interruptPrompt.mutate({ journeyShortId, chatId });
    clearActiveToolCalls();
    resetToIdleState();
  }, [interruptPrompt, journeyShortId, chatId, clearActiveToolCalls, resetToIdleState]);

  const appendUserMessage = useCallback(async (text: string, fileNames: string[] = []) => {
    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      text,
      files: fileNames,
    };

    setMessages((allMessages) => [...allMessages, userMessage]);
  }, []);

  const handleSubmitAnswers = useCallback(
    async (answers: QuestionAnswer[]) => {
      if (!chatId) return;

      setBuilding();
      answerQuestionsMutation.mutate({
        journeyShortId,
        chatId,
        answers,
      });
    },
    [chatId, journeyShortId, answerQuestionsMutation, setBuilding]
  );

  const handleAcceptPlan = useCallback(async () => {
    if (!chatId) return;

    setBuilding();
    acceptPlanMutation.mutate({
      journeyShortId,
      chatId,
    });
  }, [chatId, journeyShortId, acceptPlanMutation, setBuilding]);

  const handleRejectPlan = useCallback(async () => {
    if (!chatId) return;

    rejectPlanMutation.mutate({
      journeyShortId,
      chatId,
    });
  }, [chatId, journeyShortId, rejectPlanMutation]);

  useEffect(() => {
    if (hydrationMessages && hydrationMessages.length > 0) {
      setMessages((currentMessages) => {
        if (currentMessages.length === 0) {
          return hydrationMessages as ChatMessage[];
        }
        return currentMessages;
      });
    }
  }, [hydrationMessages]);

  const clearConversation = useCallback(async () => {
    await deleteHistoryMutation.mutateAsync(undefined, {
      onSuccess: () => {
        clearChat();
        setMessages([]);
        resetSticky();
        clearUploadedFiles();
      },
    });
  }, [deleteHistoryMutation, clearChat, resetSticky, clearUploadedFiles]);

  const { textAreaRef, resizeTextarea } = useAutoResizeTextarea({
    containerRef: contentRef,
    inputRowRef: inputRowRef,
    buttonsRowRef: buttonsRowRef,
    inputValue: chatInput,
    onInputRowHeightChange: setInputRowHeight,
  });

  const handleTranscriptionComplete = useCallback(
    (text: string) => {
      const trimmedText = text.trimEnd();
      setChatInput((currentInput) =>
        currentInput ? `${currentInput} ${trimmedText}` : trimmedText
      );
      requestAnimationFrame(() => {
        resizeTextarea();
        textAreaRef.current?.focus();
      });
    },
    [resizeTextarea, textAreaRef]
  );

  const handleTranscriptionError = useCallback(
    (error: string) => {
      setFileError(error);
    },
    [setFileError]
  );

  const handleInputSubmit = useCallback(
    (value: string) => {
      const hasActivePlan = messages.some(
        (message) =>
          message.role === 'tool' &&
          message.toolName === 'suggest_plan' &&
          (message.status === 'pending' || message.status === 'in_progress')
      );

      if (
        chatId &&
        hasActivePlan &&
        !acceptPlanMutation.isLoading &&
        !rejectPlanMutation.isLoading
      ) {
        rejectPlanMutation.mutate({
          journeyShortId,
          chatId,
        });
      }

      const files = uploadedFiles.map((item) => item.file);
      const fileNames = uploadedFiles.map((item) => item.file.name);

      appendUserMessage(value, fileNames);
      startRequest();

      runPrompt.mutate(
        {
          input: value,
          journeyShortId: journeyShortId,
          chatId,
          files,
          selectedAction: selectedNode
            ? { type: selectedNode.type, id: selectedNode.id }
            : undefined,
          mode,
        },
        {
          onError: () => {
            resetToIdleState();
          },
        }
      );

      clearActiveToolCalls();
      setThinking();

      setChatInput('');
      clearUploadedFiles();
      resetSticky();
      requestAnimationFrame(() => {
        scrollToBottom('auto');
        resizeTextarea();
        textAreaRef.current?.focus();
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      chatId,
      appendUserMessage,
      startRequest,
      runPrompt,
      journeyShortId,
      resetToIdleState,
      resizeTextarea,
      textAreaRef,
      clearUploadedFiles,
      uploadedFiles,
      selectedNode,
      clearActiveToolCalls,
      mode,
      messages,
      acceptPlanMutation.isLoading,
      rejectPlanMutation,
    ]
  );

  return (
    <Container>
      <ChatHeader
        mode={mode}
        onClose={onClose}
        onClearChat={clearConversation}
        isGenerating={isGenerating}
      />
      <Content ref={contentRef}>
        <MessagesContainer
          messages={messages}
          loadingLabel={loadingLabel}
          isBuilding={isBuilding}
          messagesRef={messagesRef}
          messagesInnerRef={messagesInnerRef}
          handleScroll={handleScroll}
          inputRowHeight={inputRowHeight}
          pauseStickyForFrame={pauseStickyForFrame}
          onSubmitAnswers={handleSubmitAnswers}
          onAcceptPlan={handleAcceptPlan}
          onRejectPlan={handleRejectPlan}
          isPlanLoading={acceptPlanMutation.isLoading}
        />
      </Content>
      <InputBox
        value={chatInput}
        onChange={setChatInput}
        onSubmit={handleInputSubmit}
        isDisabled={runPrompt.isLoading && !isGenerating}
        isGenerating={isGenerating}
        onInterrupt={handleInterruptRequest}
        textAreaRef={textAreaRef}
        inputRowRef={inputRowRef}
        buttonsRowRef={buttonsRowRef}
        onResize={resizeTextarea}
        files={uploadedFiles}
        onFileSelected={handleFileSelected}
        onFileRemove={handleRemoveFile}
        errorText={fileError || undefined}
        onClearError={clearFileError}
        selectedNode={selectedNode}
        onTranscriptionComplete={handleTranscriptionComplete}
        onTranscriptionError={handleTranscriptionError}
        mode={mode}
        onModeChange={handleModeChange}
      />
    </Container>
  );
};

const Content = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
  width: '100%',
  position: 'relative',
});

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  background: theme.color.backgroundLightDark,
  overflow: 'hidden',
  position: 'relative',
  borderRadius: pxToRem(12),
  boxShadow: theme.shadow.medium,
});

const locale = {
  en: {
    onlyImagesSupported: 'Only images are supported. {extension} is not supported.',
  },
};
